import React from 'react';
import noop from 'lodash/noop';
import { CloseOutlined } from '@ant-design/icons';
import { Form, Button, Col, Input, Row, Select, Space, Tooltip } from 'antd';
import { getAutoCompletion } from '@api/filters';
import featureNames from '@utils/featureToggle/constants';
import { SearchInput } from '@components/common';
import { map } from 'lodash';
import styles from './FilterRow.less';
import { getIcon4SelectOption } from '@src/components/common/DrillDownToEventScope';
import DimensionBetaLabel from '@src/components/dimension-panel-hz/dimensionSelectorPopup/DimensionBetaLabel';

class FilterRow extends React.PureComponent {
  constructor(props) {
    super(props);
    const { dimensions } = this.props;
    this.dimensionMap = new Map();
    dimensions.forEach((dimension) => {
      dimension.fields.forEach((field) => {
        this.dimensionMap.set(field.value, field);
      });
    });
  }

  render() {
    const { rule, features } = this.props;

    const defaultRule = {
      display: '',
      field: 'OBJECT_NAME',
      key: '',
      op: 'eq',
      value: '',
    };

    let field = this.dimensionMap.get(defaultRule.field);

    if (this.dimensionMap.get(rule.field)) {
      field = this.dimensionMap.get(rule.field);
    }
    const { operators, values, urls, autocompletion_only } = field ?? {
      operators: [],
      values: [],
      urls: {},
      autocompletion_only: false,
    };
    const showContains = features[featureNames.SHOW_CONTAINS];
    let operatorList = operators;
    if (showContains) {
      operatorList = operators.filter((operator) => !operator.value.includes('regex'));
    }
    const autoCompletion = autocompletion_only || rule.field === 'TAG';

    const { dimensions } = this.props;
    const dimesnionFields = [].concat(...map(dimensions, 'fields'));
    const ruleField = dimesnionFields.find((dimension) => dimension.value === rule.field);
    const isValidDimension = !!ruleField; // boolean value

    return (
      <>
        <Row gutter={16}>
          {this.renderDimensionDropdown(rule, isValidDimension)}
          {rule.field === 'TAG' ? this.renderTagInput(urls, rule) : null}
          {this.renderOperator(rule, operatorList, isValidDimension)}
          {this.renderValue(rule, urls, values, autoCompletion, isValidDimension)}
          {this.renderButtons()}
        </Row>
      </>
    );
  }

  onDimensionChange = (value) => {
    const { lookup, onRuleChange } = this.props;
    onRuleChange(lookup, {
      field: value,
      op: 'eq',
      key: '',
      value: '',
      display: '',
    });
  };

  onTagChange = (value) => {
    const { onRuleChange, lookup } = this.props;
    if (typeof value === 'string') {
      onRuleChange(lookup, { key: value });
    } else {
      onRuleChange(lookup, { key: value.key });
    }
  };

  onOperatorChange = (value) => {
    const { onRuleChange, lookup } = this.props;
    onRuleChange(lookup, { op: value });
  };

  onSelectValueChange = (value, option) => {
    const { onRuleChange, lookup } = this.props;
    onRuleChange(lookup, { value, display: option.props.children });
  };

  onInputValueChange = (e) => {
    const { onRuleChange, lookup } = this.props;
    onRuleChange(lookup, { value: e.target.value, display: e.target.value });
  };

  onTagValueChange = (value) => {
    const { onRuleChange, lookup } = this.props;
    onRuleChange(lookup, { value: value.key, display: value.label });
  };

  onClickDel = () => {
    const { deleteRule, lookup } = this.props;
    deleteRule(lookup);
  };

  onClickOr = () => {
    const { clickOR, lookup } = this.props;
    clickOR(lookup);
  };

  renderDimensionDropdown = (rule, isValidDimension) => {
    const { dimensions } = this.props;
    const dimensionDropDown = dimensions.map((d) => {
      const label = (
        <div>
          {getIcon4SelectOption(d.fields[0].value)}
          {d.name}
        </div>
      );
      return (
        <Select.OptGroup key={d.name} label={label}>
          {d.fields.map((f) => (
            <Select.Option key={f.value} value={f.value}>
              <Tooltip title={f.name}>
                <DimensionBetaLabel name={f.name} groupBy={f.value} />
              </Tooltip>
            </Select.Option>
          ))}
        </Select.OptGroup>
      );
    });
    return (
      <Col sm={rule.field === 'TAG' ? 5 : 7}>
        <Form.Item className={styles.formItem}>
          <Select
            defaultValue={rule.field}
            onChange={this.onDimensionChange}
            disabled={!isValidDimension}
            style={{ minWidth: 200 }}
            styles={{ popup: { root: { minWidth: 250 } } }}
          >
            {dimensionDropDown}
          </Select>
        </Form.Item>
      </Col>
    );
  };

  renderTagInput = (url, rule) => {
    const { features } = this.props;
    const { [featureNames.CUSTOM_TAGS]: customTags } = features;
    let out = (
      <Select onChange={this.onTagChange}>
        {customTags &&
          customTags.map((val) => (
            <Select.Option key={val} value={val}>
              {val}
            </Select.Option>
          ))}
      </Select>
    );
    if (customTags === null) {
      out = (
        <SearchInput
          value={{ key: rule.key, label: rule.key }}
          onChange={this.onTagChange}
          onSearch={this.autoCompletionFunc(url.key)}
          allowTyping
        />
      );
    }
    return (
      <Col sm={4}>
        <Form.Item className={styles.formItem}>{out}</Form.Item>
      </Col>
    );
  };

  renderOperator = (rule, operators, isValidDimension) => (
    <Col sm={4}>
      <Form.Item className={styles.formItem}>
        <Select value={rule.op} onChange={this.onOperatorChange} disabled={!isValidDimension}>
          {operators.map((op) => (
            <Select.Option key={op.value} value={op.value}>
              {op.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </Col>
  );

  renderButtons = () => {
    const { canDel } = this.props;
    const delButton = (
      <Button className={styles.buttonClose} onClick={this.onClickDel}>
        <CloseOutlined />
      </Button>
    );
    return (
      <Col sm={4}>
        <Space>
          <Button onClick={this.onClickOr}>OR</Button>
          {canDel && delButton}
        </Space>
      </Col>
    );
  };

  renderValue = (rule, urls, values, autoCompletion, isValidDimension) => {
    const selectOptions = values.length
      ? values.map((v) => (
          <Select.Option key={v.value} value={v.value}>
            {v.name}
          </Select.Option>
        ))
      : null;
    const select = (
      <Select defaultValue={rule.value} onChange={this.onSelectValueChange} disabled={!rule.field}>
        {selectOptions}
      </Select>
    );
    const input = (
      <Input value={rule.value} onChange={this.onInputValueChange} disabled={!isValidDimension} />
    );
    const url = rule.field === 'TAG' ? `${urls.value}?key=${rule.key}` : `${urls.value}`;
    const searchInput = (
      <SearchInput
        value={{ key: rule.value, label: rule.display }}
        onChange={this.onTagValueChange}
        onSearch={this.autoCompletionFunc(url)}
        allowTyping={rule.field === 'TAG'}
        disabled={!isValidDimension}
      />
    );
    const col = (
      <Col sm={rule.field === 'TAG' ? 7 : 9}>
        <Form.Item className={styles.formItem} disabled={!isValidDimension}>
          {values.length > 0 ? select : null}
          {!values.length && !autoCompletion ? input : null}
          {autoCompletion ? searchInput : null}
        </Form.Item>
      </Col>
    );
    return col;
  };

  autoCompletionFunc = (url) => (value) => {
    try {
      return getAutoCompletion(url, value);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
}

FilterRow.defaultProps = {
  canDel: false,
  onRuleChange: noop,
  deleteRule: noop,
  clickOR: noop,
};

export default FilterRow;
